import { useSeo } from "@/lib/seo";
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MyReports } from "@/components/MyReports";
import { Plus, Crown, Calendar, Trash2, Edit3, Eye, Gift, RefreshCw, X, CheckCircle2, Bell, BellOff, Sparkles, MessageCircle, Lock, Check, AlertCircle, User, UserSearch, Megaphone, Rss, ScanSearch, BarChart3, FileText, Home, Users, TrendingUp, History, MessageSquare, Handshake, Target } from "lucide-react";
import AgencyStats from "@/components/AgencyStats";
import PartnershipCard from "@/components/PartnershipCard";
import GestionaleDrawerContent from "@/components/dashboard/GestionaleDrawerContent";
import SlotLimitBanner from "@/components/SlotLimitBanner";
import FeedbackBanner from "@/components/FeedbackBanner";
import SocialBuyerRadar from "@/components/SocialBuyerRadar";
import AgencyProGrid from "@/components/dashboard/AgencyProGrid";
import ProDrawer from "@/components/dashboard/ProDrawer";
import ProQuickTiles from "@/components/dashboard/ProQuickTiles";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import AddonGate from "@/components/dashboard/AddonGate";
import AcquisizionePrivati from "@/pages/crm/AcquisizionePrivati";
import NuoviAnnunciPrivati from "@/pages/crm/NuoviAnnunciPrivati";
import CrmNextSeller from "@/pages/crm/CrmNextSeller";
import PropertyForm from "@/pages/PropertyForm";

const PRO_SECTIONS = {
    "acquisizione": { title: "Acquisizione Privati", subtitle: "Proprietari privati che vendono in autonomia", icon: UserSearch },
    "nuovi-privati": { title: "Nuovi Privati", subtitle: "Annunci di privati sui portali, in tempo reale", icon: Megaphone },
    "next-seller": { title: "Next Seller", subtitle: "Venditori in procinto di mettere casa sul mercato, prima degli altri", icon: Target },
    "feed": { title: "Collegamento Gestionale", subtitle: "Import feed XML/CSV ed export webhook giornaliero", icon: Rss },
    "social": { title: "Clienti Pronti sui Social", subtitle: "Radar acquirenti su Facebook e Instagram", icon: ScanSearch },
    "statistiche": { title: "Performance Statistiche", subtitle: "Annunci, visualizzazioni, contatti e conversioni", icon: BarChart3 },
    "visualizzazioni": { title: "Visualizzazioni", subtitle: "Dettaglio delle visite ai tuoi annunci", icon: Eye },
    "conversione": { title: "Conv. rate", subtitle: "Tasso di conversione dei tuoi annunci", icon: TrendingUp },
    "report": { title: "I miei report", subtitle: "Report mensili scaricabili in PDF", icon: FileText },
    "annunci": { title: "Annunci", subtitle: "Gestisci i tuoi immobili pubblicati", icon: Home },
    "aggiornamenti": { title: "Aggiornamenti annunci", subtitle: "Stato dei tuoi immobili pubblicati", icon: History },
    "contatti": { title: "Contatti ricevuti", subtitle: "Lead e richieste in un unico posto", icon: Users },
    "richieste": { title: "Richieste di contatto", subtitle: "In attesa, accettate e rifiutate", icon: MessageSquare },
    "notifiche": { title: "Notifiche email", subtitle: "Preferenze di notifica", icon: Bell },
    "partner": { title: "Codice Partner", subtitle: "Partnership e pubblicazione illimitata", icon: Handshake },
    "nuovo-annuncio": { title: "Nuovo annuncio", subtitle: "Pubblica un nuovo immobile", icon: Plus },
};

export default function Dashboard() {
    useSeo({ title: "La tua area personale | VendiDirect", description: "Gestisci annunci, abbonamento e richieste ricevute.", noindex: true });
    const { user, refresh } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sub, setSub] = useState(null);
    const [subBusy, setSubBusy] = useState(false);
    const [section, setSection] = useState(null);

    const openSection = (id) => setSection(id);
    const closeSection = useCallback(() => setSection(null), []);

    const fetch = async () => {
        try {
            const { data } = await api.get("/me/properties");
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    };
    const loadSub = async () => {
        try { const { data } = await api.get("/subscription/me"); setSub(data); } catch (e) { console.error(e); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetch(); loadSub(); refresh(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo annuncio?")) return;
        await api.delete(`/properties/${id}`);
        fetch();
    };

    const cancelSub = async () => {
        if (!window.confirm("Confermi il recesso dall'abbonamento? Il piano resterà attivo fino alla scadenza ma non si rinnoverà.")) return;
        setSubBusy(true);
        try { await api.post("/subscription/cancel"); await loadSub(); await refresh(); }
        finally { setSubBusy(false); }
    };

    const activatePromo = async (plan_id) => {
        const months = (sub?.promo?.per_plan_months || {})[plan_id] || 0;
        const msg = months > 0
            ? `Attivare ora ${months} mes${months === 1 ? "e" : "i"} gratis sul piano ${plan_id}?`
            : `Attivare l'abbonamento al piano ${plan_id}?`;
        if (!window.confirm(msg)) return;
        setSubBusy(true);
        try {
            await api.post("/subscription/activate-promo", { plan_id });
            await loadSub();
            await refresh();
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore attivazione promo");
        } finally { setSubBusy(false); }
    };

    const toggleEmailNotif = async (key, current) => {
        try {
            await api.put("/auth/profile", { [key]: !current });
            await refresh();
        } catch (e) { /* noop */ }
    };

    const isAgencyPro = user?.role === "agenzia" && sub?.plan_id === "premium";

    if (!user) return <div className="pt-32 text-center text-ink-400">Effettua l'accesso per accedere alla dashboard.</div>;

    // Admin non ha dashboard utente: redirect immediato al pannello amministrativo
    if (user.is_admin && !user.is_impersonating) {
        return <Navigate to="/admin" replace />;
    }

    // Paywall: plan expired → block dashboard until payment (skip for admins and impersonation)
    if (sub?.plan_expired && !user.is_impersonating && !user.is_admin && !sub?.is_admin_bypass) {
        return (
            <div data-testid="dashboard-paywall" className="pt-32 pb-24 max-w-2xl mx-auto px-6 text-center">
                <div className="border border-amber-600/40 bg-amber-900/10 p-10">
                    <Crown size={36} className="text-gold mx-auto mb-4" />
                    <span className="label-eyebrow text-gold">Abbonamento scaduto</span>
                    <h1 className="font-serif text-3xl text-ink-50 mt-3">
                        {sub.is_promo ? "Il tuo periodo gratuito è terminato" : "Il tuo abbonamento è scaduto"}
                    </h1>
                    <p className="text-ink-100 mt-4 leading-relaxed">
                        {sub.is_promo
                            ? `Hai completato i mesi gratuiti sul piano ${sub.plan_name}. Per continuare ad accedere alla Dashboard, ai tuoi annunci e alle chat, attiva il pagamento.`
                            : `Il rinnovo non è stato completato. Per continuare ad accedere alla Dashboard, ai tuoi annunci e alle chat, aggiorna il metodo di pagamento.`}
                    </p>
                    <p className="text-xs text-ink-400 mt-4">
                        I tuoi annunci sono conservati in modalità pausa. Nessun dato è stato eliminato.
                    </p>
                    <Link
                        to="/prezzi"
                        data-testid="dashboard-paywall-cta"
                        className="mt-8 inline-flex items-center gap-2 cta-gold cta-gold-pulse px-8 py-3.5 text-sm"
                    >
                        <Crown size={14} /> Attiva pagamento — €{(sub.plan_price != null ? sub.plan_price : 9.9).toFixed(2).replace(".", ",")}/mese →
                    </Link>
                    <p className="text-[11px] text-ink-400 mt-5">
                        Pagamento sicuro tramite Stripe. Cancellabile in ogni momento.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="dashboard-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="mb-16">
                <span className="label-eyebrow">Dashboard</span>
                <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 mt-3">Ciao, {user.name?.split(" ")[0]}</h1>
                <p className="text-ink-400 mt-3">Gestisci i tuoi annunci, l'abbonamento e le preferenze.</p>
                {user.is_impersonating && (
                    <button
                        data-testid="dashboard-stop-impersonate"
                        onClick={async () => {
                            try {
                                await api.post("/admin/impersonate/stop");
                                const me = await api.get("/auth/me");
                                if (me.data?.user?.is_impersonating) throw new Error("Sessione non pulita");
                            } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); return; }
                            window.location.replace("/admin");
                        }}
                        className="mt-5 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 rounded-full"
                    >
                        ← Torna alla vista admin
                    </button>
                )}
                <div className="mt-8">
                    <DashboardHeader sub={sub} user={user} busy={subBusy} onCancel={cancelSub} onActivatePromo={activatePromo} onToggle={toggleEmailNotif} />
                </div>
                {isAgencyPro && <ProQuickTiles sub={sub} properties={items} onOpenSection={openSection} />}
            </div>

            {user?.role === "agenzia" && <SlotLimitBanner user={user} />}

            <FeedbackBanner />

            {isAgencyPro ? (
                <>
                    <AgencyProGrid user={user} onToggleEmail={toggleEmailNotif} onOpenSection={openSection} />
                    <ProDrawer
                        open={!!section}
                        onClose={closeSection}
                        title={PRO_SECTIONS[section]?.title}
                        subtitle={PRO_SECTIONS[section]?.subtitle}
                        icon={PRO_SECTIONS[section]?.icon}
                    >
                        {section === "acquisizione" && <AddonGate module="acquisizione"><AcquisizionePrivati /></AddonGate>}
                        {section === "nuovi-privati" && <AddonGate module="nuovi_privati"><NuoviAnnunciPrivati /></AddonGate>}
                        {section === "next-seller" && <AddonGate module="next_seller"><CrmNextSeller onGoToZones={closeSection} /></AddonGate>}
                        {section === "feed" && <GestionaleDrawerContent />}
                        {section === "social" && <SocialBuyerRadar />}
                        {(section === "statistiche" || section === "visualizzazioni" || section === "conversione") && <AgencyStats />}
                        {section === "report" && <MyReports />}
                        {(section === "contatti" || section === "richieste") && <ContactRequestsInbox />}
                        {section === "notifiche" && <NotificationPreferences user={user} onToggle={toggleEmailNotif} />}
                        {section === "partner" && <PartnershipCard />}
                        {(section === "annunci" || section === "aggiornamenti") && <MyListings items={items} loading={loading} onRemove={remove} onNew={() => setSection("nuovo-annuncio")} />}
                        {section === "nuovo-annuncio" && <PropertyForm mode="create" embedded onSuccess={() => { closeSection(); fetch(); }} />}
                    </ProDrawer>
                </>
            ) : (
                <>
                    {user?.role === "agenzia" && user?.plan_id !== "agenzia" && <PartnershipCard />}

                    <NotificationPreferences user={user} onToggle={toggleEmailNotif} />

                    <ContactRequestsInbox />

                    <div className="flex items-end justify-between mb-8 mt-16">
                        <div>
                            <span className="label-eyebrow">Performance</span>
                            <h2 className="font-serif text-3xl text-ink-50 mt-2">Statistiche</h2>
                        </div>
                    </div>
                    <AgencyStats />

                    {user?.role === "agenzia" && <MyReports />}

                    <MyListings items={items} loading={loading} onRemove={remove} />
                </>
            )}
        </div>
    );
}

function MyListings({ items, loading, onRemove, onNew }) {
    return (
        <div data-testid="my-listings-section">
            <div className="flex items-end justify-between mb-8 mt-20">
                <div>
                    <span className="label-eyebrow">I tuoi annunci</span>
                    <h2 className="font-serif text-3xl text-ink-50 mt-2">{items.length} pubblicati</h2>
                </div>
                {onNew ? (
                    <button type="button" onClick={onNew} data-testid="new-listing-button" className="px-6 py-3 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors flex items-center gap-2">
                        <Plus size={14} /> Nuovo annuncio
                    </button>
                ) : (
                    <Link to="/pubblica" data-testid="new-listing-button" className="px-6 py-3 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors flex items-center gap-2">
                        <Plus size={14} /> Nuovo annuncio
                    </Link>
                )}
            </div>

            {loading ? (
                <div className="text-ink-400">Caricamento...</div>
            ) : items.length === 0 ? (
                <div className="border border-white/10 p-16 text-center text-ink-400">
                    Non hai ancora pubblicato nessun annuncio.
                </div>
            ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                    {items.map((p) => (
                        <div key={p.id} data-testid={`my-listing-${p.id}`} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition-colors">
                            <img src={p.photos?.[0]} alt="" className="col-span-2 aspect-[4/3] object-cover" />
                            <div className="col-span-5">
                                <div className="font-serif text-lg text-ink-50">{p.title}</div>
                                <div className="text-xs text-ink-400 mt-1">{p.city} · {p.property_type}</div>
                            </div>
                            <div className="col-span-2 font-serif text-price">{formatPrice(p.price)}</div>
                            <div className="col-span-1 text-xs text-ink-400 flex items-center gap-1"><Eye size={12} /> {p.views || 0}</div>
                            <div className="col-span-2 flex justify-end gap-2">
                                <Link to={`/annunci/${p.id}/modifica`} data-testid={`edit-listing-${p.id}`} className="p-2 hover:text-gold text-ink-100"><Edit3 size={14} /></Link>
                                <button data-testid={`delete-listing-${p.id}`} onClick={() => onRemove(p.id)} className="p-2 hover:text-red-400 text-ink-100"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function ContactRequestsInbox() {
    const [tab, setTab] = useState("pending"); // pending | accepted | declined
    const [items, setItems] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async (status = tab) => {
        setLoading(true);
        try {
            const { data } = await api.get("/contact-requests/received", { params: { status_filter: status } });
            setItems(data.items || []);
            setPendingCount(data.pending_count || 0);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(tab); }, [tab]);

    const decide = async (id, action) => {
        setBusyId(id);
        try {
            await api.post(`/contact-requests/${id}/decide`, { action });
            await load(tab);
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore");
        } finally {
            setBusyId(null);
        }
    };

    const fmtDate = (s) => {
        try { return new Date(s).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); }
        catch { return s; }
    };

    return (
        <div data-testid="contact-requests-inbox" className="mb-16">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                    <span className="label-eyebrow">Privacy & Consenso</span>
                    <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-3">
                        Richieste di contatto
                        {pendingCount > 0 && (
                            <span data-testid="pending-count-badge" className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 bg-gold text-ink-950 text-xs font-bold rounded-full">
                                {pendingCount}
                            </span>
                        )}
                    </h2>
                </div>
                <div className="flex gap-2 text-xs">
                    {["pending", "accepted", "declined"].map((t) => (
                        <button
                            key={t}
                            data-testid={`tab-${t}`}
                            onClick={() => setTab(t)}
                            className={`px-3 py-1.5 border transition-colors ${tab === t ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:border-white/30"}`}
                        >
                            {t === "pending" ? "In attesa" : t === "accepted" ? "Accettate" : "Rifiutate"}
                        </button>
                    ))}
                    <button
                        data-testid="refresh-requests"
                        onClick={() => load(tab)}
                        className="px-3 py-1.5 border border-white/15 text-ink-400 hover:text-gold hover:border-gold/40 transition-colors"
                        aria-label="Aggiorna"
                    >
                        <RefreshCw size={12} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink-400 border border-white/10 p-6">Caricamento richieste...</div>
            ) : items.length === 0 ? (
                <div data-testid="no-requests" className="border border-white/10 p-10 text-center text-sm text-ink-400">
                    {tab === "pending"
                        ? "Nessuna richiesta in attesa. Quando un utente abbonato chiederà il tuo consenso per chat o contatti, comparirà qui."
                        : tab === "accepted"
                            ? "Nessuna richiesta accettata."
                            : "Nessuna richiesta rifiutata."}
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <div key={r.id} data-testid={`request-${r.id}`} className="border border-white/10 bg-ink-900/30 p-5">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        {r.level === "chat" ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-blue-400/30 text-blue-200 bg-blue-900/20">
                                                <MessageCircle size={10} /> Chat
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-gold/30 text-gold bg-gold/5">
                                                <Lock size={10} /> Contatti
                                            </span>
                                        )}
                                        {r.status !== "pending" && (
                                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.status === "accepted" ? "text-green-300 border border-green-700/40 bg-green-900/20" : "text-red-300 border border-red-700/40 bg-red-900/20"}`}>
                                                {r.status === "accepted" ? "Accettata" : "Rifiutata"}
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-serif text-lg text-ink-50">{r.property_title}</div>
                                    <div className="text-xs text-ink-400 mt-1">
                                        Da <span className="text-ink-100">{r.requester_name || "Utente"}</span> · {fmtDate(r.created_at)}
                                    </div>
                                    {r.message && (
                                        <blockquote className="mt-3 pl-3 border-l-2 border-gold/30 text-sm text-ink-100 italic">
                                            "{r.message}"
                                        </blockquote>
                                    )}
                                </div>
                                {r.status === "pending" ? (
                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            data-testid={`accept-${r.id}`}
                                            onClick={() => decide(r.id, "accept")}
                                            disabled={busyId === r.id}
                                            className="px-4 py-2 bg-gold text-ink-950 text-xs font-medium hover:bg-amber-300 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            <Check size={14} /> Accetta
                                        </button>
                                        <button
                                            data-testid={`decline-${r.id}`}
                                            onClick={() => decide(r.id, "decline")}
                                            disabled={busyId === r.id}
                                            className="px-4 py-2 border border-white/20 text-ink-100 text-xs hover:border-red-400 hover:text-red-300 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                        >
                                            <X size={14} /> Rifiuta
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 self-center">
                                        {r.status === "accepted" && r.level === "chat" && (
                                            <Link
                                                to={`/chat?to=${encodeURIComponent(r.requester_id || "")}&property=${encodeURIComponent(r.property_id)}&title=${encodeURIComponent(r.property_title || "")}`}
                                                data-testid={`open-chat-${r.id}`}
                                                className="cta-gold px-4 py-2 bg-gold text-ink-950 text-xs font-medium hover:bg-amber-300 transition-colors flex items-center gap-1.5"
                                            >
                                                <MessageCircle size={14} /> Apri chat
                                            </Link>
                                        )}
                                        <Link
                                            to={`/annunci/${r.property_id}`}
                                            className="text-xs text-ink-400 hover:text-gold underline"
                                        >
                                            Vedi annuncio
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4 flex items-start gap-2 text-xs text-ink-400 border-l-2 border-gold/30 pl-3">
                <AlertCircle size={14} className="text-gold mt-0.5 shrink-0" />
                <span>
                    Per ogni richiesta ricevi anche un'email con link rapidi di Accetta/Rifiuta. Le tue decisioni qui sono equivalenti.
                </span>
            </div>
        </div>
    );
}

function NotificationPreferences({ user, onToggle }) {
    const chatOn = user?.email_notify_chat !== false;
    const listingOn = user?.email_notify_listing_update !== false;
    return (
        <div className="mb-16">
            <span className="label-eyebrow">Notifiche</span>
            <h2 className="font-serif text-2xl text-ink-50 mt-2 mb-5">Preferenze email</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                    data-testid="toggle-email-chat"
                    onClick={() => onToggle("email_notify_chat", chatOn)}
                    className={`border p-5 text-left flex items-start gap-4 hover:border-gold transition-colors ${chatOn ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-900/30"}`}
                >
                    <div className="mt-0.5">{chatOn ? <Bell size={18} className="text-gold" /> : <BellOff size={18} className="text-ink-400" />}</div>
                    <div className="flex-1">
                        <div className="font-medium text-ink-50 text-sm flex items-center gap-2">
                            Notifiche chat
                            {chatOn ? <CheckCircle2 size={14} className="text-gold" /> : <span className="text-xs text-ink-400">Off</span>}
                        </div>
                        <p className="text-xs text-ink-400 mt-1">Ricevi un'email ogni volta che arriva un nuovo messaggio in chat.</p>
                    </div>
                </button>
                <button
                    data-testid="toggle-email-listing"
                    onClick={() => onToggle("email_notify_listing_update", listingOn)}
                    className={`border p-5 text-left flex items-start gap-4 hover:border-gold transition-colors ${listingOn ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-900/30"}`}
                >
                    <div className="mt-0.5">{listingOn ? <Bell size={18} className="text-gold" /> : <BellOff size={18} className="text-ink-400" />}</div>
                    <div className="flex-1">
                        <div className="font-medium text-ink-50 text-sm flex items-center gap-2">
                            Aggiornamenti annunci
                            {listingOn ? <CheckCircle2 size={14} className="text-gold" /> : <span className="text-xs text-ink-400">Off</span>}
                        </div>
                        <p className="text-xs text-ink-400 mt-1">Riepilogo mensile sullo stato dei tuoi annunci pubblicati.</p>
                    </div>
                </button>
            </div>
        </div>
    );
}


